import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Card, Row, Col, TimePicker, Switch, Typography, Button, Space, Tag, message, Tooltip, Divider, Modal, Input, Select, Radio, Progress, Alert, DatePicker } from 'antd';
import { DeleteOutlined, PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import apiService from '@/services/apiService';

type TimeSlot = { time: string; enabled: boolean };

type JobProgress = {
  platform: string
  status: 'idle' | 'running' | 'completed' | 'error'
  startTime?: string
  endTime?: string
  totalAccounts: number
  processedAccounts: number
  currentAccount?: string
  totalOrders?: number
  processedOrders?: number
  currentOrder?: string
  errorMessage?: string
}

const HKTVmallJobAndStatus: React.FC = () => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([{ time: '19:00', enabled: true }]);
  const [saving, setSaving] = useState<boolean>(false);
  const [running, setRunning] = useState<boolean>(false);
  const [runVisible, setRunVisible] = useState<boolean>(false);
  const [storeOptions, setStoreOptions] = useState<string[]>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [tagName, setTagName] = useState<string>('');
  const [selectedTag, setSelectedTag] = useState<string | undefined>(undefined);
  const [tagsMap, setTagsMap] = useState<Record<string, string[]>>({});
  const tagOptions = useMemo(() => Object.keys(tagsMap).map(n => ({ label: n, value: n })), [tagsMap])
  const [progress, setProgress] = useState<JobProgress | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

  useEffect(()=>{
    (async()=>{
      try{
        const data:any = await apiService.get('/schedules');
        const rec = (data?.data||[]).find((r:any)=> r.platform==='HKTVmall');
        if(rec){
          setEnabled(!!rec.enabled);
          // 後端尚未支援多時段時，回退為單時段
          const t = (rec.daily_time || '19:00').slice(0,5);
          const slotsFromApi = Array.isArray(rec.timeSlots) ? rec.timeSlots : [];
          let normalized: TimeSlot[] = [];
          if(slotsFromApi.length){
            if(typeof slotsFromApi[0] === 'string'){
              normalized = (slotsFromApi as string[]).map(s=> ({ time: String(s).slice(0,5), enabled: true }));
            }else{
              normalized = (slotsFromApi as any[]).map(o=> ({ time: String((o&&o.time)!=null? o.time : o).slice(0,5), enabled: 'enabled' in (o||{}) ? !!o.enabled : true }));
            }
          }
          if(!normalized.length){
            normalized = [{ time: t, enabled: true }];
          }
          setTimeSlots(normalized);
        }
      }catch{}
    })();
  },[]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const fetchProgress = async () => {
    try {
      const res: any = await apiService.get('/schedules/HKTVmall/progress');
      if (res?.success && res?.data) {
        setProgress(res.data);

        // If completed or error, stop polling
        if (res.data.status === 'completed') {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          setRunning(false);

          // Check if no orders were found
          if (!res.data.totalOrders || res.data.totalOrders === 0) {
            message.info('訂單拉取完成！沒有找到新訂單。');
          } else {
            message.success('訂單拉取完成！');
          }
        } else if (res.data.status === 'error') {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          setRunning(false);
          message.error(`訂單拉取失敗: ${res.data.errorMessage || '未知錯誤'}`);
        }
      }
    } catch (err) {
      console.error('Failed to fetch progress:', err);
    }
  };

  const startProgressPolling = () => {
    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    // Initial fetch
    fetchProgress();

    // Poll every 1 second
    progressIntervalRef.current = setInterval(fetchProgress, 1000);
  };

  const stopProgressPolling = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const loadAccounts = async()=>{
    try{
      const res:any = await apiService.get('/integrations/hktvmall/accounts');
      const list = Array.isArray(res?.data) ? res.data : [];
      const codes = list
        .filter((a:any)=> (a?.status === 'connected') && (a?.isEnabled !== false))
        .map((a:any)=> String(a.storeCode))
        .filter(Boolean);
      setStoreOptions(Array.from(new Set(codes)));
    }catch{
      setStoreOptions([]);
    }
  }

  const loadSavedTags = async () => {
    try{
      const res:any = await apiService.listHKTVRunTags();
      const list: Array<{ tagName: string; storeCodes: string[] }> = Array.isArray(res?.data) ? res.data : []
      const map: Record<string, string[]> = {}
      list.forEach(r=>{ if(r.tagName) map[r.tagName] = Array.isArray(r.storeCodes) ? r.storeCodes : [] })
      setTagsMap(map)
    }catch{ setTagsMap({}); }
  }

  const saveCurrentAsTag = async () => {
    const name = (tagName || '').trim();
    if(!name){ message.warning('請先輸入標籤名稱'); return }
    if(selectedStores.length===0){ message.warning('請先選擇 Store Code'); return }
    try{
      await apiService.upsertHKTVRunTag({ tagName: name, storeCodes: Array.from(new Set(selectedStores)) })
      await loadSavedTags()
      setSelectedTag(name)
      message.success('標籤已保存')
    }catch{ message.error('保存失敗') }
  }

  const openRunModal = async()=>{
    setRunVisible(true);
    setSelectedStores([]);
    setSelectedTag(undefined);
    setSelectedDate(null); // 重置日期選擇
    await loadAccounts();
    loadSavedTags();
  }

  const save = async()=>{
    try{ setSaving(true);
      const cleaned = (timeSlots || []).map(s => ({ time: /^\d{2}:\d{2}$/.test(String(s.time).trim()) ? String(s.time).trim() : '19:00', enabled: !!s.enabled }));
      const enabledTimes = cleaned.filter(s=> s.enabled).map(s=> s.time).slice(0, 6);
      if (cleaned.length > 6) {
        message.warning('最多可新增 6 個時段，已自動截取前 6 個');
      }
      // 兼容老後端：同時提交 dailyTime 與 timeSlots（後端忽略未知字段亦可）
      await apiService.put(`/schedules/${encodeURIComponent('HKTVmall')}`, { enabled: !!enabled, dailyTime: (enabledTimes[0] || '19:00'), timeSlots: enabledTimes });
      message.success('已保存');
    }catch{ message.error('保存失敗'); }
    finally{ setSaving(false); }
  };

  const runNow = async()=>{
    // 打開彈窗讓用戶選擇 Store Code / 標籤
    await openRunModal();
  };

  const confirmRun = async()=>{
    const codes = (selectedStores && selectedStores.length)
      ? selectedStores
      : (selectedTag && tagsMap[selectedTag])
        ? tagsMap[selectedTag]
        : []
    if(codes.length===0){ message.warning('請至少選擇一個 Store Code 或選擇一個標籤'); return }
    try{
      setRunning(true);
      setProgress(null); // Reset progress
      const body:any = { storeCodes: codes };
      const tagList = (selectedTag || tagName) ? [String(selectedTag || tagName)] : [];
      if(tagList.length) body.tags = tagList;
      // 添加日期參數（如果有選擇日期的話）
      if(selectedDate) {
        body.date = selectedDate.format('YYYY-MM-DD');
      }
      await apiService.post(`/schedules/${encodeURIComponent('HKTVmall')}/run`, body);
      message.success('已觸發手動拉單');
      setRunVisible(false);

      // Start polling for progress
      startProgressPolling();
    }catch{ message.error('觸發失敗'); setRunning(false); }
  }

  const selectAllStores = () => {
    if (!storeOptions.length) {
      message.warning('目前沒有可用的 Store Code');
      return;
    }
    setSelectedTag(undefined);
    setTagName('');
    setSelectedStores(storeOptions);
    message.success('已全選所有 Store Code');
  };

  const clearAllStores = () => {
    setSelectedStores([]);
  };

  const accountsPercent = progress && progress.totalAccounts > 0
    ? Math.round((progress.processedAccounts / progress.totalAccounts) * 100)
    : 0;

  const ordersPercent = progress && progress.totalOrders && progress.totalOrders > 0
    ? Math.round(((progress.processedOrders || 0) / progress.totalOrders) * 100)
    : 0;

  return (
    <Card>
      <Row align="middle" justify="space-between">
        <Col>
          <Typography.Title level={3} style={{ marginBottom: 8 }}>連線狀態</Typography.Title>
          <Typography.Title level={2} style={{ marginTop: 0 }}>{enabled ? 'Enabled' : 'Disabled'}</Typography.Title>
          <div style={{ marginTop: 8 }}>{enabled ? <Tag color="green">啟用</Tag> : <Tag>停用</Tag>}</div>
        </Col>
        <Col>
          <Space>
            <Button type="primary" onClick={save} loading={saving}>保存</Button>
            <Button onClick={runNow} loading={running} disabled={progress?.status === 'running'}>手動拉單</Button>
          </Space>
        </Col>
      </Row>

      {/* Progress Display */}
      {progress && progress.status === 'running' && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <Alert
            message="訂單拉取進行中"
            description={
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Typography.Text strong>帳號進度:</Typography.Text>
                  <Progress
                    percent={accountsPercent}
                    status="active"
                    format={() => `${progress.processedAccounts}/${progress.totalAccounts}`}
                  />
                  {progress.currentAccount && (
                    <Typography.Text type="secondary">當前帳號: {progress.currentAccount}</Typography.Text>
                  )}
                </div>
                {progress.totalOrders && progress.totalOrders > 0 && (
                  <div>
                    <Typography.Text strong>訂單進度:</Typography.Text>
                    <Progress
                      percent={ordersPercent}
                      status="active"
                      format={() => `${progress.processedOrders || 0}/${progress.totalOrders || 0}`}
                    />
                    {progress.currentOrder && (
                      <Typography.Text type="secondary">當前訂單: {progress.currentOrder}</Typography.Text>
                    )}
                  </div>
                )}
              </Space>
            }
            type="info"
            showIcon
            icon={<LoadingOutlined />}
          />
        </>
      )}

      {/* Error Display */}
      {progress && progress.status === 'error' && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <Alert
            message="訂單拉取失敗"
            description={progress.errorMessage || '未知錯誤'}
            type="error"
            showIcon
            closable
            onClose={() => setProgress(null)}
          />
        </>
      )}

      {/* Success Display */}
      {progress && progress.status === 'completed' && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <Alert
            message="訂單拉取完成"
            description={
              (!progress.totalOrders || progress.totalOrders === 0)
                ? `已檢查 ${progress.processedAccounts} 個帳號，沒有找到新訂單`
                : `成功處理 ${progress.processedAccounts} 個帳號，${progress.processedOrders || 0} 個訂單`
            }
            type={(!progress.totalOrders || progress.totalOrders === 0) ? "info" : "success"}
            showIcon
            closable
            onClose={() => setProgress(null)}
          />
        </>
      )}

      <Divider style={{ margin: '16px 0' }} />

      <Row gutter={[16, 16]} align="middle">
        <Col span={24}>
          <Typography.Text strong>連接/斷開</Typography.Text>
        </Col>
        <Col span={24}>
          <Tooltip title="切換為『連接』後，系統會依下方『已啟用』時段自動拉取 HKTVmall 訂單；切換為『斷開』時將不會自動拉單（仍可手動拉單）。">
            <Switch checked={enabled} onChange={setEnabled} checkedChildren="連接" unCheckedChildren="斷開" />
          </Tooltip>
        </Col>
      </Row>

      <Divider style={{ margin: '16px 0' }} />

      <Row align="middle" justify="space-between" style={{ marginBottom: 8 }}>
        <Col>
          <Typography.Text strong>時段列表</Typography.Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<PlusOutlined />} onClick={()=> setTimeSlots(prev=> {
              if ((prev?.length || 0) >= 6) { message.warning('最多可新增 6 個時段'); return prev; }
              return [...prev, { time: '19:00', enabled: true }];
            })}>新增時段</Button>
            <Button onClick={()=> setTimeSlots(prev=> prev.map(s=> ({ ...s, enabled: true })))}>一鍵啟用全部</Button>
            <Button onClick={()=> setTimeSlots(prev=> prev.map(s=> ({ ...s, enabled: false })))}>一鍵停用全部</Button>
          </Space>
        </Col>
      </Row>

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {timeSlots.map((slot,idx)=> (
          <Row key={idx} gutter={[12, 12]} align="middle">
            <Col>
              <Switch checked={!!slot.enabled} onChange={(v)=> setTimeSlots(prev=> prev.map((x,i)=> i===idx ? ({ ...x, enabled: !!v }) : x))} checkedChildren="啟用" unCheckedChildren="停用" />
            </Col>
            <Col>
              <TimePicker value={slot.time ? (dayjs(slot.time,'HH:mm') as unknown as Dayjs) : null as unknown as Dayjs} format="HH:mm" minuteStep={5}
                onChange={(t)=> setTimeSlots(prev=> t ? prev.map((x,i)=> i===idx ? ({ ...x, time: t.format('HH:mm') }) : x) : prev.filter((_,i)=> i!==idx))} />
            </Col>
            <Col>
              <Button type="text" danger icon={<DeleteOutlined />} onClick={()=> setTimeSlots(prev=> prev.filter((_,i)=> i!==idx))}>刪除</Button>
            </Col>
          </Row>
        ))}
      </Space>

      <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
        啟用後，系統會在每日所設時間（HKT）自動拉取 HKTVmall 訂單；亦可點擊「手動拉單」立即執行一次。
      </Typography.Paragraph>

      {/* 手動拉單彈窗 */}
      <Modal
        title="手動拉單"
        open={runVisible}
        onOk={confirmRun}
        onCancel={()=> setRunVisible(false)}
        okText="開始拉單"
        confirmLoading={running}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {/* 日期選擇區 */}
          <div>
            <Typography.Text strong>選擇日期（可選）</Typography.Text>
            <div style={{ marginTop: 8 }}>
              <DatePicker
                style={{ width: '100%' }}
                placeholder="選擇拉單日期（不選則使用今天）"
                value={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                format="YYYY-MM-DD"
                allowClear
              />
            </div>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              提示：如果選擇日期，會拉取該日期的訂單；否則使用今天的日期
            </Typography.Text>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* 已保存標籤區 */}
          <div>
            <Typography.Text strong>選擇標籤</Typography.Text>
            <div style={{ marginTop: 8 }}>
              {tagOptions.length ? (
                <Radio.Group
                  value={selectedTag}
                  onChange={(e)=>{
                    const v = e.target.value as string
                    setSelectedTag(v)
                    setTagName(v)
                    if (tagsMap[v]) setSelectedStores(tagsMap[v])
                  }}
                  optionType="button"
                  buttonStyle="solid"
                  options={tagOptions}
                />
              ) : (
                <Typography.Text type="secondary">尚未保存任何標籤，請先在下方建立</Typography.Text>
              )}
            </div>
            <Space style={{ marginTop: 8 }}>
              <Button onClick={()=>{ setSelectedTag(undefined); setTagName(''); }}>清空標籤</Button>
            </Space>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* 新建/覆蓋標籤 */}
          <div>
            <Typography.Text strong>建立/覆蓋標籤</Typography.Text>
            <Space wrap style={{ marginTop: 8 }}>
              <Input
                placeholder="輸入標籤名稱（如：8小時、test1）"
                style={{ width: 280 }}
                value={tagName}
                onChange={(e)=> setTagName(e.target.value)}
                allowClear
              />
              <Button type="dashed" onClick={saveCurrentAsTag} disabled={!selectedStores.length}>保存為標籤</Button>
              <Typography.Text type="secondary">當前已選 {selectedStores.length} 個 Store Code</Typography.Text>
            </Space>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Typography.Text strong>選擇 Store Code（可多選）</Typography.Text>
              <Space>
                <Button onClick={selectAllStores} disabled={!storeOptions.length}>全選Store Code</Button>
                <Button onClick={clearAllStores} disabled={!selectedStores.length}>清除選擇</Button>
              </Space>
            </div>
            <div style={{ marginTop: 8 }}>
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                placeholder="選擇 Store Code"
                value={selectedStores}
                onChange={(vals)=> setSelectedStores(vals as string[])}
                options={storeOptions.map(code=> ({ label: code, value: code }))}
                maxTagCount={8}
              />
            </div>
          </div>

          <div>
            <Typography.Text type="secondary">提示：選擇標籤後會自動帶入其下的 Store Code；也可直接多選 Store Code 後保存為新標籤。</Typography.Text>
          </div>
        </Space>
      </Modal>
    </Card>
  );
};

export default HKTVmallJobAndStatus;


